import React, { useState, useEffect } from "react";
import "./NewPatientRegistration.css";

const DoctorMaster = ({ screenAction, triggerSave }) => {
  const [isDisabled, setIsDisabled] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Store fetched departments for the dropdown
  const [departmentsList, setDepartmentsList] = useState([]);

  const initialFormState = {
    doc_cd: "",
    doctor_name: "",
    department: "",
    specialization: "",
    qualification: "",
    contact_no: "",
    email: "",
    consultation_fee: "",
    status: "Active",
    _isExisting: false
  };

  const [formData, setFormData] = useState(initialFormState);

  // --- NEW: GENERATE FORMATTED DOCTOR ID ---
  const getNewDocId = () => {
    const date = new Date();
    return `DOC-${date.getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`;
  };

  // --- INITIAL DATA LOAD (Departments) ---
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        // ✅ FIXED: Using the live HTTPS API you provided
        const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/departments?employee_id=${user?.id}`);
        const data = await res.json();
        // Only load Active departments for selection
        setDepartmentsList(data.filter(d => d.status === "Active"));
      } catch (err) {
        console.error("Failed to load departments", err);
      }
    };
    fetchDepartments();
  }, []);

  // --- 1. SEARCH LOGIC ---
  const searchDoctors = async (text) => {
    setSearchText(text);
    if (text.length < 2) {
      setDoctors([]);
      setShowDropdown(false);
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      // Make sure your backend has this route set up!
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/doctors/search?q=${text}&employee_id=${user?.id}`);
      const data = await res.json();
      setDoctors(data);
      setShowDropdown(true);
    } catch (err) {
      console.error("Search error:", err);
    }
  };

  const selectDoctor = (doc) => {
    setFormData({
      ...doc,
      _isExisting: true
    });
    setSearchText(`${doc.doctor_name} | ${doc.doc_cd}`);
    setShowDropdown(false);

    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
  };

  // --- 2. FORM ACTIONS ---
  const clearForm = () => {
    setFormData({
      ...initialFormState,
      doc_cd: getNewDocId()
    });
    setSearchText("");
    setIsDisabled(false);
  };

  useEffect(() => {
    if (triggerSave > 0 && screenAction === "save") handleSubmit();
  }, [triggerSave]);

  useEffect(() => {
    if (screenAction === "add") clearForm();
    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
    if (screenAction === "refresh") clearForm();
    if (screenAction === "print") printForm();
  }, [screenAction]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // --- 3. SAVE LOGIC ---
  const handleSubmit = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!formData.doctor_name || !formData.department) {
        return alert("Doctor Name and Department are required!");
    }

    const payload = {
      ...formData,
      created_by: user?.id
    };
    
    // Remove the tracking flag before sending to the backend
    delete payload._isExisting;

    const method = formData._isExisting ? "PUT" : "POST";
    const url = method === "POST" 
      ? "https://hmsapi.emedhatechnologies.com/master/api/api/doctors" 
      : `https://hmsapi.emedhatechnologies.com/master/api/api/doctors/${formData.doc_cd}`;

    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        alert("Doctor Profile Saved Successfully!");
        if (method === "POST") clearForm();
      } else {
        alert("Error saving doctor profile");
      }
    } catch (err) {
      alert("Network error while saving.");
    }
  };

  // --- 4. PRINT FORM ---
  const printForm = () => {
    const newWindow = window.open("", "", "width=900,height=700");
    const today = new Date().toLocaleDateString();

    const htmlContent = `
      <html>
        <head>
          <title>Doctor Profile - ${formData.doctor_name}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #333; margin: 0; padding: 40px; }
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #2980b9; padding-bottom: 10px; margin-bottom: 20px; }
            .hospital-info h1 { margin: 0; color: #2c3e50; font-size: 24px; text-transform: uppercase; }
            .hospital-info p { margin: 2px 0; font-size: 12px; color: #7f8c8d; }
            .report-title { text-align: right; }
            .report-title h2 { margin: 0; color: #2980b9; font-size: 18px; }
            .section-header { background: #ecf0f1; padding: 8px 12px; font-weight: bold; font-size: 14px; border-left: 4px solid #2980b9; margin-top: 20px; margin-bottom: 10px; text-transform: uppercase; }
            .data-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 10px; }
            .data-item { font-size: 13px; display: flex; border-bottom: 1px dotted #ccc; padding: 6px 0; }
            .label { font-weight: 600; width: 160px; color: #555; }
            .value { flex: 1; color: #000; font-weight: 500; }
            .footer { margin-top: 50px; font-size: 10px; text-align: center; color: #95a5a6; border-top: 1px solid #eee; padding-top: 10px; }
          </style>
        </head>
        <body onload="window.print(); window.close();">
          <div class="header">
            <div class="hospital-info">
              <h1>EMEDHA CARE HOSPITAL</h1>
              <p>Doctor Master Record</p>
            </div>
            <div class="report-title">
              <h2>DOCTOR PROFILE</h2>
              <p>Print Date: ${today}</p>
            </div>
          </div>

          <div class="section-header">Professional Information</div>
          <div class="data-grid">
            <div class="data-item"><span class="label">Doctor Code:</span> <span class="value">${formData.doc_cd}</span></div>
            <div class="data-item"><span class="label">Status:</span> <span class="value">${formData.status}</span></div>
            <div class="data-item"><span class="label">Doctor Name:</span> <span class="value">${formData.doctor_name}</span></div>
            <div class="data-item"><span class="label">Department:</span> <span class="value">${formData.department}</span></div>
            <div class="data-item"><span class="label">Qualification:</span> <span class="value">${formData.qualification || 'N/A'}</span></div>
            <div class="data-item"><span class="label">Specialization:</span> <span class="value">${formData.specialization || 'N/A'}</span></div>
          </div>

          <div class="section-header">Contact & Billing</div>
          <div class="data-grid">
            <div class="data-item"><span class="label">Contact Number:</span> <span class="value">${formData.contact_no || 'N/A'}</span></div>
            <div class="data-item"><span class="label">Email Address:</span> <span class="value">${formData.email || 'N/A'}</span></div>
            <div class="data-item"><span class="label">Consultation Fee:</span> <span class="value">₹ ${parseFloat(formData.consultation_fee || 0).toFixed(2)}</span></div>
          </div>

          <div class="footer">
            System Generated Report - Emedha HMS
          </div>
        </body>
      </html>
    `;

    newWindow.document.write(htmlContent);
    newWindow.document.close();
  };

  return (
    <div className="registration-container fade-in">
      
      {/* SEARCH BAR */}
      {(screenAction === "edit" || screenAction === "view") && (
        <div className="patient-search">
          <label>Search Doctor</label>
          <input
            type="text"
            value={searchText}
            placeholder="Search by ID, Name, or Dept..."
            onChange={(e) => searchDoctors(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (doctors.length === 1) selectDoctor(doctors[0]);
              }
            }}
            autoFocus
          />
          {showDropdown && doctors.length > 0 && (
            <div className="patient-dropdown">
              {doctors.map((doc) => (
                <div key={doc.doc_cd} className="patient-item" onClick={() => selectDoctor(doc)}>
                  {doc.doctor_name} | ID: {doc.doc_cd} | {doc.department}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <form className="luxury-form">
        
        {/* IDENTIFICATION */}
        <div className="form-section-header">
          <h3>Professional Information</h3>
        </div>
        <div className="form-grid">
          <div className="input-box read-only">
            <label>Doctor Code</label>
            <input type="text" value={formData.doc_cd} readOnly placeholder="Auto Generated" />
          </div>
          <div className="input-box">
            <label>Doctor Name <span style={{color:'red'}}>*</span></label>
            <input type="text" name="doctor_name" value={formData.doctor_name} onChange={handleChange} disabled={isDisabled} placeholder="e.g., Dr. Ramesh Patel" />
          </div>
          
          <div className="input-box">
            <label>Department <span style={{color:'red'}}>*</span></label>
            <select name="department" value={formData.department} onChange={handleChange} disabled={isDisabled}>
              <option value="">Select Department</option>
              {departmentsList.map(dept => (
                <option key={dept.dept_cd} value={dept.dept_name}>
                  {dept.dept_name}
                </option>
              ))}
            </select>
          </div>

          <div className="input-box">
            <label>Status</label>
            <select name="status" value={formData.status} onChange={handleChange} disabled={isDisabled}>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="On Leave">On Leave</option>
            </select>
          </div>
          
          <div className="input-box">
            <label>Qualification</label>
            <input type="text" name="qualification" value={formData.qualification} onChange={handleChange} disabled={isDisabled} placeholder="e.g., MBBS, MD" />
          </div>
          <div className="input-box">
            <label>Specialization</label>
            <input type="text" name="specialization" value={formData.specialization} onChange={handleChange} disabled={isDisabled} placeholder="e.g., Pediatric Cardiology" />
          </div>
        </div>

        {/* DETAILS & CONTACT */}
        <div className="form-section-header">
          <h3>Contact & Billing</h3>
        </div>
        <div className="form-grid">
          <div className="input-box">
            <label>Contact Number</label>
            <input type="text" name="contact_no" value={formData.contact_no} onChange={handleChange} disabled={isDisabled} placeholder="Phone No." />
          </div>
          <div className="input-box">
            <label>Email Address</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} disabled={isDisabled} placeholder="doctor@hospital.com" />
          </div>
          <div className="input-box">
            <label>Consultation Fee (₹)</label>
            <input type="number" name="consultation_fee" value={formData.consultation_fee} onChange={handleChange} disabled={isDisabled} placeholder="e.g., 500" />
          </div>
        </div>

      </form>
    </div>
  );
};

export default DoctorMaster;